/**
 * ETag-aware request/response interceptor for the four polling endpoints
 * the offline frontend depends on. See design §5.5 / plan issue 3.5.
 *
 * Behaviour, gated entirely behind `enable_offline_checks`:
 *
 *   1. Request: if the request URL ends with one of the configured
 *      polling suffixes and we have a stored ETag for it, attach
 *      `If-None-Match`.
 *   2. Response, status 200: if the response carries an ETag header,
 *      store the (etag, body) tuple in IndexedDB keyed by URL.
 *   3. Response, status 304: replace the empty body with the cached
 *      data so the calling code sees a normal 200-shaped response.
 *      Transparent — Redux actions and hooks don't need to know.
 *   4. Network error: if the path is one of the configured suffixes and
 *      we have a cached body, resolve with the cached body (synthetic
 *      200). The page can render config from disk while offline.
 *
 * The four configured suffixes match the backend ETag middleware exactly
 * (see `backend/main.py` / `backend/middleware/etag.py`). Anything else
 * passes through untouched.
 *
 * Interceptors run outside React, so they read the master flag from the
 * module-level mirror in `./featureFlag.ts`, which is kept in sync by
 * <OfflineServiceWorker /> on every render of the React tree.
 */

import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { getOfflineDB, STORE_ETAG_CACHE } from "./db";
import { getOfflineFeatureEnabled } from "./featureFlag";
import type { ETagCacheEntry } from "./types";

/**
 * Path suffixes for endpoints that opt into ETag caching. Mirrors the
 * `path_suffixes` argument passed to `ETagMiddleware` in
 * `backend/main.py`. Keep these two lists in sync — if a new polling
 * endpoint is added, both sides need updating.
 */
export const ETAG_CACHED_PATH_SUFFIXES = [
    "/configs/active",
    "/form-field-options/all",
    "/defects/all",
    "/offline-cache/",
] as const;

export function pathIsETagCached(url: string | undefined | null): boolean {
    if (!url) return false;
    // Strip query string before suffix-matching: a query parameter must not
    // un-match an otherwise-cached path.
    const path = url.split("?")[0];
    return ETAG_CACHED_PATH_SUFFIXES.some((suffix) => path.endsWith(suffix));
}

/**
 * Canonical etag-cache key for a request URL. The same logical endpoint
 * arrives at this interceptor under two different URL forms in our
 * codebase:
 *
 *   - Absolute (`https://avos.local:8443/api/tenants/<tid>/configs/active`)
 *     — produced by `callAvosApi` (which prepends `base_url`) and by
 *     `getCachedETagForUrl` consumers like `CreateLayer.tsx`.
 *   - Relative (`tenants/<tid>/configs/active`) — produced by Redux
 *     thunks dispatched through the redux-axios middleware that don't
 *     prepend `base_url`.
 *
 * Without normalization, the same response gets cached twice under
 * different keys, and subsequent reads only hit one of them. The capture
 * flow in `CreateLayer.tsx` reads the absolute key — which the warmer
 * writes once at login and never refreshes — while the 6-minute redux
 * polls update the relative key. Result: a stale `config_hash` if the
 * tenant config changes mid-session.
 *
 * The normalization here strips scheme/host and any leading `/api/` so
 * both forms collapse to a single key. New format is suffix-style, e.g.
 * `tenants/<tid>/configs/active` — matches the Redux form, which is the
 * shorter of the two.
 */
export function normalizeETagCacheKey(url: string): string {
    const path = url.split("?")[0];
    return path
        .replace(/^https?:\/\/[^/]+/, "") // strip scheme + host:port
        .replace(/^\/api\//, "") // strip leading /api/
        .replace(/^\//, ""); // strip leading slash
}

async function readEntry(url: string): Promise<ETagCacheEntry | undefined> {
    const db = await getOfflineDB();
    return db.get(STORE_ETAG_CACHE, normalizeETagCacheKey(url));
}

/**
 * Helper exposed for the offline session capture flow (issue 5.2). The
 * session's `config_hash` is the ETag of the most recent
 * `GET /api/tenants/{tid}/configs/active` response — set by the
 * response interceptor above on the same exchange.
 *
 * Returns null if nothing has been cached yet, e.g. on a brand-new
 * device that has never been online for this tenant. Callers should
 * treat null as "couldn't capture a config_hash" and either refuse
 * the offline scan or proceed with an empty hash (the design
 * preserves data on a config-hash mismatch, so an empty hash is
 * conservative — replay will likely mark the session failed).
 */
export async function getCachedETagForUrl(url: string): Promise<string | null> {
    try {
        const entry = await readEntry(url);
        return entry?.etag ?? null;
    } catch {
        return null;
    }
}

async function writeEntry(url: string, etag: string, data: unknown): Promise<void> {
    const key = normalizeETagCacheKey(url);
    const db = await getOfflineDB();
    await db.put(STORE_ETAG_CACHE, { url: key, etag, data });
}

function readETagHeader(headers: Record<string, unknown> | undefined): string | undefined {
    if (!headers) return undefined;
    // Axios lowercases response header keys, but be defensive.
    const v = (headers as Record<string, unknown>).etag ?? (headers as Record<string, unknown>).ETag;
    return typeof v === "string" ? v : undefined;
}

function syntheticResponse<T = unknown>(
    config: InternalAxiosRequestConfig,
    data: T,
): AxiosResponse<T> {
    return {
        data,
        status: 200,
        statusText: "OK (offline cache)",
        headers: {},
        config,
        request: undefined,
    } as AxiosResponse<T>;
}

/**
 * Wire request + response interceptors onto the given axios instance. The
 * `isEnabled` callback is invoked at request time so a runtime flag flip
 * (e.g. tenant switch) is honoured without re-wiring the instance.
 *
 * Defaults `isEnabled` to the module-level mirror via
 * `getOfflineFeatureEnabled`; tests pass an explicit fake.
 */
export function installETagInterceptor(
    instance: AxiosInstance,
    isEnabled: () => boolean = getOfflineFeatureEnabled,
): void {
    instance.interceptors.request.use(async (config) => {
        if (!isEnabled() || !pathIsETagCached(config.url)) return config;
        try {
            const entry = await readEntry(config.url!);
            if (entry?.etag) {
                config.headers.set("If-None-Match", entry.etag);
            }
        } catch {
            // A cache read failure must never block the request; just send
            // it without If-None-Match and the interceptor will populate
            // the cache from a fresh 200.
        }
        return config;
    });

    instance.interceptors.response.use(
        async (response) => {
            if (!isEnabled() || !response.config?.url) return response;
            if (!pathIsETagCached(response.config.url)) return response;
            if (response.status !== 200) return response;
            const etag = readETagHeader(response.headers as Record<string, unknown>);
            if (etag) {
                try {
                    await writeEntry(response.config.url, etag, response.data);
                } catch {
                    // Cache write errors are non-fatal — the response is
                    // already in flight back to the caller.
                }
            }
            return response;
        },
        async (error) => {
            const config = error?.config as InternalAxiosRequestConfig | undefined;
            if (!isEnabled() || !config?.url || !pathIsETagCached(config.url)) {
                return Promise.reject(error);
            }

            // Server returned 304 — the body is empty by spec. Substitute
            // the cached body so callers see a normal 200-shaped response.
            if (error.response?.status === 304) {
                const entry = await readEntry(config.url);
                if (entry) {
                    return syntheticResponse(config, entry.data);
                }
                // No cached body for a 304 is an unusual state (the server
                // shouldn't have sent us a 304 without us sending
                // If-None-Match in the first place). Fall through and let
                // the caller re-fetch.
            }

            // Network error (no error.response): if we have a cached body,
            // serve it. This is the offline-fallback path that lets the
            // page render config from disk while offline.
            if (!error.response) {
                try {
                    const entry = await readEntry(config.url);
                    if (entry) {
                        return syntheticResponse(config, entry.data);
                    }
                } catch {
                    // fall through
                }
            }

            return Promise.reject(error);
        },
    );
}
